export type QuotaUnit = "ITEMS" | "SECONDS"
export type FraudStatus = "PENDING" | "PASSED" | "FAILED"
export type QcStatus = "PENDING" | "PASSED" | "FAILED"
export type RecordingStatus = "RECORDING" | "COMPLETE" | "ABANDONED" | "FAILED" | "PARTIALLY_UPLOADED"
export type ContractStatus = "DRAFT" | "ACTIVE" | "SUSPENDED" | "CLOSED"
export type DeliveryStatus = "AVAILABLE" | "RESERVED" | "DELIVERED"
export type DeliveryBatchStatus = "RESERVED" | "BUILDING" | "READY" | "DELIVERED" | "FAILED" | "CANCELLED" | "DISPUTED"
export type CustomerVerdict = "ACCEPTED" | "REJECTED" | null
export type CutMode = "full" | "matches" | "multiplayer"
export type DeliverySelectionStatus =
  | "PENDING"
  | "PLANNING"
  | "DRAFT"
  | "REVIEWING"
  | "APPROVED"
  | "EXECUTED"
  | "DISCARDED"
  | "FAILED"
export type SelectionReviewVerdict = "PENDING" | "LEGIT" | "REJECTED"
export type ValidationSeverity = "PASS" | "WARN" | "FAIL"

export interface Entitlement {
  id: string
  gameIds: number[]
  regions: string[]
  tags: string[]
  utmSource: string | null
  dateFrom: string | null
}

// Delivery destination. Empty/null means the derived Buff S3 bucket.
export interface StorageConfig {
  type?: "S3" | "GCS"
  // On a PATCH, `null` clears the key; an absent key keeps the stored value.
  bucket?: string | null
  bucketOwnerFullControl?: boolean | null
  // Writes sessions.json (co-play grouping) without cutting — a co-play
  // delivery of whole recordings needs this on.
  includeSessionManifest?: boolean
  excludeChatScore?: boolean
}

// How recordings are trimmed before delivery. On a PATCH, `null` clears the
// stored value while omitting the key leaves it untouched.
export interface CutConfig {
  cutMode: CutMode
  minPlayers: number | null
  pipelineVersion: number | null
}

export type GameplayCutProfile = "soft" | "soft_plus" | "harsh_lenient" | "harsh_strict"
export type GameplayCutOutputMode = "manifest" | "trimmed"
export const GAMEPLAY_CUT_CLASSES = [
  "live_play",
  "in_match_ui",
  "no_control",
  "match_transition",
  "pre_post_match",
  "outside_game",
] as const
export type GameplayCutClass = (typeof GAMEPLAY_CUT_CLASSES)[number]
export type GameplayCutKeep = Partial<Record<GameplayCutClass, boolean>>

export interface GameplayCutConfig {
  profile?: GameplayCutProfile
  keep?: GameplayCutKeep
  idleThresholdSec?: number
  shortBreakSec?: number | null
  minBlockSec?: number
  coplayMinSec?: number
  coplayMinPlayers?: number
  outputMode?: GameplayCutOutputMode
}

export interface ResolvedGameplayCut {
  profile: GameplayCutProfile
  keep: Record<GameplayCutClass, boolean>
  idleThresholdSec: number
  shortBreakSec: number | null
  minBlockSec: number
  coplayMinSec: number
  coplayMinPlayers: number
  outputMode: GameplayCutOutputMode
}

export interface Contract {
  id: string
  clientId: string
  name: string
  status: ContractStatus
  // Structured key that selects the customer bucket (source of truth).
  customerKey: string
  // Derived, read-only: `${prefix}-${region}-${customerKey}`.
  deliveryBucket: string | null
  quotaUnit: QuotaUnit
  maxItems: number | null
  maxSeconds: string | null
  watermark: boolean
  storageConfig: StorageConfig | null
  cutConfig: CutConfig
  gameplayCut: GameplayCutConfig | null
  // Commercial selection terms the co-play planner selects against.
  selectionPolicy: SelectionPolicy | null
  startsAt: string | null
  endsAt: string | null
  externalRef: string | null
  entitlements: Entitlement[]
  updatedAt?: string
}

// Result of preflighting a contract's delivery destination. `remediation` names
// the terragrunt key to add — provisioning stays in terraform.
export interface ContractDestination {
  contractId: string
  provider: "S3" | "GCS"
  bucket: string
  reachable: boolean
  // Null when the write probe was not requested — "not tested", not "not writable".
  writable: boolean | null
  error: string | null
  remediation: string | null
}

export interface UnitBalance {
  quota: string | null
  reserved: string
  delivered: string
  remaining: string | null
  overDelivered: string
}

export interface Balance {
  contractId: string
  clientId: string
  quotaUnit: QuotaUnit
  quota: string | null
  reserved: string
  delivered: string
  consumed: string
  remaining: string | null
  overDelivered: string
  items: UnitBalance
  seconds: UnitBalance
  byGame: Array<{ gameId: number; deliveredItems: number; reservedItems: number }>
}

export interface DataItem {
  id: string
  recordingId: string
  gameId: number
  gameName: string | null
  accountId: string | null
  region: string | null
  source: string | null
  recordedAt: string
  durationSec: number | null
  recordCount: number
  byteSize: string
  s3Prefix: string
  recordingStatus: RecordingStatus
  fraudStatus: FraudStatus
  qcStatus: QcStatus
  tags: string[]
  utmSource: string | null
  utmCampaign: string | null
  deliverable: boolean
  deliveryStatus: DeliveryStatus
}

export interface DeliveryItem {
  id: string
  itemId: string
  recordingId: string
  gameId: number
  status: DeliveryStatus
  recordCount: number
  byteSize: string
  durationSec: number | null
  customerStatus: CustomerVerdict
  customerRejectionReason: string | null
  deliveredAt: string | null
}

export interface Batch {
  id: string
  clientId: string
  contractId: string
  status: DeliveryBatchStatus
  quotaUnit: QuotaUnit
  itemCount: number
  // Total delivered duration in seconds — the primary tracking unit ("delivery hours").
  reservedSeconds: string
  // Total delivered content size in bytes (sum of this batch's items).
  totalBytes: string
  gamesTouched: number[]
  s3Bucket: string | null
  deliveryPrefix: string | null
  manifestS3Key: string | null
  receiptS3Key: string | null
  // Present only when the contract asked for a co-play session manifest.
  sessionsS3Key: string | null
  // Snapshotted at reserve, so a contract edit never alters an in-flight build.
  watermark: boolean
  includeInputStreams: boolean
  includeEventLogs: boolean
  includeDeviceMetadata: boolean
  failureReason: string | null
  reservedItems: number
  reservedAt: string
  deliveredAt: string | null
}

// Result of inspecting the customer's dedicated S3 prefix for a built batch.
export interface S3Verification {
  bucket: string
  prefix: string
  recordingCount: number
  totalBytes: string
  manifestPresent: boolean
  receiptPresent: boolean
  objectsCopied: boolean
  verified: boolean
  error: string | null
}

export interface BatchDetail {
  batch: Batch | null
  // One PAGE of items: a deliverAll batch can hold 50,000, which no single
  // ledger reply may carry. `itemCount` is the whole batch.
  items: DeliveryItem[]
  itemCount: number
  itemsByGame: Record<number, number>
  limit: number
  offset: number
  s3Verification: S3Verification | null
}

export interface SelectionTotals {
  count: number
  records: number
  bytes: string
  durationSec: number
}

export interface SelectionSummary {
  selection: SelectionTotals
  ineligibleItemIds: string[]
  previouslyDelivered: SelectionTotals
  remaining: string | null
  quotaUnit: string
  fits: boolean
}

export interface GameOption {
  gameId: number
  gameName: string
  gameSlug: string | null
  count: number
}

export interface Facets {
  regions: string[]
  tags: string[]
  utmSources: string[]
}

// The ledger reports a failed sync-on-search rather than silently serving the
// pool it already had.
export interface DataItemSearchMeta {
  poolStale?: boolean
  poolStaleReason?: string
}

export interface SearchDataItemsQuery {
  contractId?: string
  gameId?: number
  accountId?: string
  accountEmail?: string
  recordingId?: string
  region?: string
  fraudStatus?: FraudStatus
  qcStatus?: QcStatus
  deliveryStatus?: DeliveryStatus
  tags?: string[]
  utmSource?: string
  recordedFrom?: string
  recordedTo?: string
  deliverableOnly?: boolean
  limit?: number
  offset?: number
}

export interface ListResponse<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

// Composer filters carried into "Deliver all eligible" so it honours the active filter.
export interface ReserveFilters {
  gameId?: number
  accountId?: string
  accountEmail?: string
  recordingId?: string
  region?: string
  utmSource?: string
  tags?: string[]
  recordedFrom?: string
  recordedTo?: string
}

export interface EntitlementFilter {
  gameIds?: number[]
  regions?: string[]
  tags?: string[]
  utmSource?: string
  dateFrom?: string
}

export interface CreateContractBody {
  // clientId is generated internally by the backend — never sent from the UI.
  name: string
  quotaUnit: QuotaUnit
  maxItems?: number
  maxSeconds?: number
  // Bucket is derived from this key server-side; no free-text bucket.
  customerKey: string
  entitlements?: EntitlementFilter[]
  watermark?: boolean
  storageConfig?: StorageConfig
  cutConfig?: Partial<CutConfig>
  gameplayCut?: GameplayCutConfig | null
  selectionPolicy?: SelectionPolicy
  startsAt?: string
  endsAt?: string
}

export interface SelectionTier {
  name: string
  gameIds: number[]
}

// Every field optional: unset falls back to the contract's stored policy, then
// to the ledger defaults. Tiers are declared most-valuable-first.
export interface SelectionPolicy {
  targetHours?: number
  eligibleGameIds?: number[]
  tiers?: SelectionTier[]
  effectiveFrom?: string
  minSessionDurationMs?: number
  // Validation-only ceiling: sessions are selected at full length and this is
  // what a delivered session gets flagged against.
  maxSessionDurationMs?: number
  minPlayers?: number
  maxPlayers?: number
  minConfidence?: number
  perGameCapHours?: number
  perGameRaisedCapHours?: number
  raisedCap3pPlusPct?: number
  perGameMinHours?: number
  perGameBatchCapHours?: number
  trustedCountryCodes?: string[]
  excludeRecovered?: boolean
  excludeRecordingIds?: string[]
  seedSessionIds?: string[]
}

export interface SelectionGameStat {
  gameId: number
  gameName: string | null
  tier: string
  sessions: number
  playerHours: number
  contractCap: number
  deliveredHours: number
  remainingHours: number
}

export interface SelectionStats {
  targetHours: number
  selectedHours: number
  sessionCount: number
  recordingCount: number
  gameCount: number
  squadHours: { twoPlayer: number; threePlayer: number; fourPlusPlayer: number }
  tierHours: Record<string, number>
  byGame: SelectionGameStat[]
  // Games a pre-included seed pushed past their remaining contract cap.
  capOverrunGameIds?: number[]
  // Why candidates were dropped, so a thin selection is explainable.
  excluded: {
    eligibleSessions: number
    noMembers: number
    notReservable: number
    operatorExcluded: number
    recovered: number
    eligibilityUnknown: number
    gamepadOrLobby: number
    // Sessions whose closure reaches a session that was itself excluded: the
    // shared recording could only ship as half a co-play window.
    closureIncomplete?: number
  }
}

export interface Selection {
  id: string
  clientId: string
  contractId: string
  name: string
  status: DeliverySelectionStatus
  // The resolved policy this run used, frozen at plan time.
  config: SelectionPolicy
  // Null until the async plan completes (PENDING / PLANNING / FAILED).
  stats: SelectionStats | null
  sessionCount: number
  recordingCount: number
  playerHours: number
  // Recordings with no data item yet — not deliverable until data items sync.
  // Capped by the ledger; missingDataItemCount is the true size.
  missingDataItemRecordingIds: string[]
  missingDataItemCount: number
  sampleSize: number | null
  // Capped by the ledger; batchCount is the true size.
  batchIds: string[]
  batchCount: number
  failureReason: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
  executedAt: string | null
}

export interface SelectionSession {
  sessionId: string
  gameId: number
  gameName: string | null
  tier: string
  pipelineVersion: number
  playerCount: number
  durationMs: number
  playerHours: number
  utcStartMs: string
  utcEndMs: string
  recordingIds: string[]
  accountIds: string[]
  // Pre-built multi-pane replay link for the reviewer.
  replayUrl: string | null
  sampled: boolean
  verdict: SelectionReviewVerdict
  reviewNote: string | null
  reviewedBy: string | null
  reviewedAt: string | null
}

export interface ReviewProgress {
  sampled: number
  pending: number
  legit: number
  rejected: number
}

// Planning is async: this returns the PENDING row, and the caller polls
// getSelection until the status leaves PENDING/PLANNING.
export interface PlanSelectionResult {
  selection: Selection
}

export interface SelectionDetail {
  selection: Selection | null
  sessions: SelectionSession[]
  total: number
  limit: number
  offset: number
  review: ReviewProgress
}

export interface ExecuteSelectionResult {
  selection: Selection
  // Bounded preview — batchCount is the real total.
  batchIds: string[]
  batchCount: number
  batchIdsTruncated: boolean
  itemCount: number
  rejectedSessionCount: number
  unresolvedSessionCount: number
}

export interface ValidationCheck {
  key: string
  label: string
  severity: ValidationSeverity
  count: number
  samples: string[]
  detail: string | null
}

export interface ValidateBatchResult {
  batchId: string
  bucket: string
  deliveryPrefix: string | null
  manifestPresent: boolean
  sessionsPresent: boolean
  receiptPresent: boolean
  // True when nothing came back FAIL. WARNs do not block.
  passed: boolean
  checks: ValidationCheck[]
}

export interface UpdateContractBody {
  name?: string
  status?: ContractStatus
  quotaUnit?: QuotaUnit
  maxItems?: number
  maxSeconds?: number
  externalRef?: string
  watermark?: boolean
  storageConfig?: StorageConfig
  cutConfig?: Partial<CutConfig>
  gameplayCut?: GameplayCutConfig | null
  selectionPolicy?: SelectionPolicy
  startsAt?: string
  endsAt?: string
  // REPLACES the existing entitlement set.
  entitlements?: EntitlementFilter[]
}

// Result of republishing a delivered batch's JSON artifacts. The batch itself is
// unchanged; `status` is echoed so the UI can show it stayed DELIVERED.
export interface RebuildManifestResult {
  batchId: string
  status: string
  deliveryPrefix: string | null
  bucket: string | null
  writtenKeys: string[]
  rebuiltAt: string
  reason: string
}

export const CUT_DETECT_LEVELS = ["events", "partial", "config_only", "none"] as const
export type CutDetectLevel = (typeof CUT_DETECT_LEVELS)[number]
export const CUT_LIST_STATUSES = ["delivered", "new", "reserve", "extra_reserve"] as const
export type CutListStatus = (typeof CUT_LIST_STATUSES)[number]

export interface CutTitleRuleInput {
  title: string
  archetype: number
  allowedModes: string
  excludedModes: string[]
  softExtraCuts: string[]
  harshExtraCuts: string[]
  detect: CutDetectLevel
  listStatus: CutListStatus
}

export interface CutTitleRule extends CutTitleRuleInput {
  gameId: number
  updatedAt: string
}

export interface RecordingCutFlagInput {
  voidReason: string
  note?: string
}

export interface RecordingCutFlag {
  recordingId: string
  voidReason: string
  note: string | null
  flaggedBy: string | null
  createdAt: string
  updatedAt: string
}

export type GameplayCutPreviewPartners = "any_member" | "targets_only"

export interface GameplayCutPreviewBody {
  recordingIds: string[]
  partners?: GameplayCutPreviewPartners
  contractId?: string
  gameplayCut?: GameplayCutConfig
  pipelineVersion?: number | null
}

export interface CutPiece {
  ptsStartMs: number
  ptsEndMs: number
}

export interface CutKeptInterval extends CutPiece {
  billedMs: number
  pieces: CutPiece[]
}

export interface GameplayCutPreviewRecording {
  recordingId: string
  found: boolean
  ptsOffsetMs?: number
  profile?: GameplayCutProfile | "custom"
  outputMode?: GameplayCutOutputMode
  keptMs?: number
  keptIntervals?: CutKeptInterval[]
  blockCount?: number
  pieceCount?: number
  recordedMs?: number
  cutMsByClass?: Record<GameplayCutClass, number>
  outsideGameMsByCause?: { idle: number; unfocused: number }
  droppedUnderMinMs?: number
  notCoplayMs?: number
  unavailableClasses?: GameplayCutClass[]
  idleKnown?: boolean
  voidedMs?: number
  voidReason?: string | null
  joinedBreakMs?: number
  unappliedRules?: string[]
}

export interface GameplayCutPreviewConfig extends Omit<ResolvedGameplayCut, "profile"> {
  profile: GameplayCutProfile | "custom"
  pipelineVersion: number | null
}

export interface GameplayCutPreviewResult {
  partners?: GameplayCutPreviewPartners
  config: GameplayCutPreviewConfig
  recordings: GameplayCutPreviewRecording[]
}
